import { ChevronRight } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Avatar } from './avatar'
import { Chip, DueChip, TicketChip } from './chip'
import { Tooltip } from './tooltip'

export interface QueueRowProps {
  id: string
  /** Conduit name; `<CORP CODE> - Name` for condo corps. */
  customer: string
  /** Muted note after the customer, e.g. "account not resolved" when `customer` is the Desk contact. */
  customerHint?: string
  /** Account or contact link. The click must not also select the row. */
  renderCustomer?: (customer: string) => React.ReactNode
  title: string
  tickets: (string | number)[]
  /** Render a ticket badge (the app's badge with its copy/open menu); defaults to a plain TicketChip. */
  renderTicket?: (ticket: string | number) => React.ReactNode
  owner?: { name: string }
  due?: { label: string; overdue?: boolean }
  draftReady?: boolean
  /** Riley's one-line rolling summary. */
  summary?: string
  nextMove?: string
  chips?: React.ReactNode
  /** Row actions in normal flow beside line 2, so they can never cover the title or chips. */
  actions?: React.ReactNode
  /** Phone: a control under the summary, in normal flow, so it cannot cover the customer or the case. */
  footer?: React.ReactNode
  /** #876 a reply this person has not seen: bold row, a dot and "New reply" with the time (contractor: other dot). */
  unread?: { label: string; kind?: 'customer' | 'contractor' | null }
  selected?: boolean
  expanded?: boolean
  /** `Space` expands the row inline: last turn + draft. */
  expandedContent?: React.ReactNode
  onSelect?: () => void
  onOpen?: () => void
}

/**
 * Queue row (scope §5.1, #877). Line 1: the case title, larger. Line 2: the customer, smaller and muted. Then Desk
 * numbers, owner, due, draft and chips. Last: Riley's summary and the next move, in italic subtle text so it reads as Riley's.
 */
export function QueueRow({
  id,
  customer,
  customerHint,
  renderCustomer,
  title,
  tickets,
  renderTicket,
  owner,
  due,
  draftReady,
  summary,
  nextMove,
  chips,
  actions,
  footer,
  unread,
  selected = false,
  expanded = false,
  expandedContent,
  onSelect,
  onOpen,
}: QueueRowProps) {
  const firstTicket = tickets[0]
  const extraTickets = tickets.slice(1)
  return (
    <div
      role="option"
      aria-selected={selected}
      id={`row-${id}`}
      data-row-id={id}
      data-unread={unread ? (unread.kind ?? 'customer') : undefined}
      tabIndex={selected ? 0 : -1}
      onClick={(event) => {
        if (event.target instanceof Element && event.target.closest('[data-testid="queue-row-actions"]')) return
        onSelect?.()
      }}
      onDoubleClick={(event) => {
        if (event.target instanceof Element && event.target.closest('[data-testid="queue-row-actions"]')) return
        onOpen?.()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen?.()
      }}
      className={cn(
        'group relative cursor-pointer border-b border-border px-4 py-3 outline-none transition-colors hover:bg-muted/60',
        selected && 'bg-accent hover:bg-accent before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary',
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div data-testid="queue-row-title" className="flex min-w-0 flex-1 items-baseline gap-1.5">
          {unread ? (
            <span
              aria-hidden
              data-testid="queue-row-unread-dot"
              className={cn(
                'size-2 shrink-0 self-center rounded-full',
                unread.kind === 'contractor' ? 'bg-audience-contractor' : 'bg-primary',
              )}
            />
          ) : null}
          <p
            data-testid="queue-row-case-title"
            className={cn('min-w-0 truncate text-base text-foreground', unread ? 'font-bold' : 'font-semibold')}
          >
            {title}
          </p>
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      <p data-testid="queue-row-customer" className="mt-0.5 min-w-0 truncate text-xs text-muted-foreground">
        {renderCustomer ? renderCustomer(customer) : customer}
        {customerHint ? <span className="ml-2 text-subtle">{customerHint}</span> : null}
      </p>
      <div className="mt-1.5 flex min-w-0 items-center gap-2">
        <div data-testid="queue-row-meta" className="flex min-w-0 flex-1 flex-nowrap items-center gap-1.5 overflow-hidden">
          {firstTicket !== undefined ? (
            renderTicket ? (
              <span className="shrink-0">{renderTicket(firstTicket)}</span>
            ) : (
              <TicketChip number={firstTicket} />
            )
          ) : null}
          {extraTickets.length > 0 ? (
            <Tooltip
              content={
                <span className="block max-w-72 whitespace-normal">
                  {extraTickets.map((ticket) => (
                    <span key={ticket} className="block">
                      #{ticket}
                    </span>
                  ))}
                </span>
              }
            >
              <Chip tone="outline" className="tabular font-mono text-[11px]" aria-label={`${extraTickets.length} more tickets`}>
                +{extraTickets.length}
              </Chip>
            </Tooltip>
          ) : null}
          {owner ? (
            <span className="shrink-0">
              <Avatar name={owner.name} size="sm" />
            </span>
          ) : null}
          {unread ? (
            <Chip
              tone={unread.kind === 'contractor' ? 'neutral' : 'primary'}
              className="shrink-0 font-semibold"
              data-testid="queue-row-unread"
            >
              {unread.label}
            </Chip>
          ) : null}
          {due ? <DueChip label={due.label} overdue={due.overdue} /> : null}
          {draftReady ? (
            <Chip tone="primary" className="shrink-0">
              Draft ready
            </Chip>
          ) : null}
          <span className="flex min-w-0 flex-1 overflow-hidden">{chips}</span>
          <button
            type="button"
            data-testid="queue-row-open"
            aria-label={`Open ${customer}`}
            title="Open (Enter)"
            onClick={(e) => {
              e.stopPropagation()
              onOpen?.()
            }}
            className="shrink-0 cursor-pointer rounded p-0.5 text-subtle hover:bg-muted hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>
      {summary || nextMove ? (
        <p className="mt-1 truncate text-xs italic text-muted-foreground">
          {summary}
          {summary && nextMove ? ' · ' : ''}
          {nextMove ? <span className="not-italic text-foreground/70">Next: {nextMove}</span> : null}
        </p>
      ) : null}
      {footer ? (
        <div className="mt-2" data-testid="queue-row-actions">
          {footer}
        </div>
      ) : null}
      {expanded && expandedContent ? <div className="mt-3 rounded-md border border-border bg-card p-3">{expandedContent}</div> : null}
    </div>
  )
}

export function QueueList({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="listbox" aria-label={label} className="flex-1 overflow-y-auto">
      {children}
    </div>
  )
}
